import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FP610 } from '../../services/p251Api';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import { useP251Mode } from './hooks/useP251Mode';
import { useP251Record } from './hooks/useP251Record';
import { useP251Navigation } from './hooks/useP251Navigation';
import { recalcDetail, recalcTotals } from './helpers';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Detail from './components/Tab2Detail';
import Tab3GrandUnion from './components/Tab3GrandUnion';
import ActionBar from './components/ActionBar';
import './P251Form.css';

const TABS = ['基本資料', '明細', 'GRAND UNION'];

const P251Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const permissions = useModulePermission(Modules.P251);

  // ── Hooks ──
  const { mode, setMode, isEditing } = useP251Mode();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    setDetailFieldWithCalc, recalcAll,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow, insertDetailRow,
    revertToRecord, resetForAdd, applyResult,
  } = useP251Record();

  const loadRecordWrapped = useCallback(async (id: string) => {
    await loadRecord(id);
  }, [loadRecord]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useP251Navigation(loadRecordWrapped);

  // Derived: PCS mode hides MTS-related fields (BR-C05)
  const isPCS = (formData.fp610_27 as number) === 3;

  // ── Initial Load ──
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load P251 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ──
  const handleAdd = useCallback(() => {
    if (isEditing) {
      // Save mode
      Modal.confirm({
        title: '存檔確認',
        content: '請確認儲存本資料!',
        okText: '確認',
        cancelText: '取消',
        onOk: async () => {
          await handleSave(mode, (result: FP610) => {
            applyResult(result);
            setMode('browse');
            // Reload list to include the new record
            loadList().then(ids => {
              const idx = ids.indexOf(result.fp610_01);
              if (idx >= 0) setCurrentIdx(idx);
            });
          });
        },
      });
    } else {
      // New add mode
      resetForAdd();
      setMode('add');
      setActiveTab(0);
    }
  }, [isEditing, mode, handleSave, applyResult, setMode, resetForAdd, loadList, setCurrentIdx]);

  const handleModify = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else if (record) {
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '刪除確認',
      content: '請確認刪除本資料!',
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecord(ids[newIdx]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleExit = useCallback(() => {
    navigate('/erp');
  }, [navigate]);

  // ── Ship unit change (BR-C05) ──
  const handleShipUnitChange = useCallback((val: number) => {
    setField('fp610_27', val);
    // Recalculate after changing unit
    setTimeout(() => recalcAll(), 0);
  }, [setField, recalcAll]);

  // ── Knit type change: recalculate all (BR-C01) ──
  const handleKnitTypeChange = useCallback(() => {
    setTimeout(() => recalcAll(), 0);
  }, [recalcAll]);

  // ── MTS rate / GW rate change: recalculate all ──
  const handleMTSRateChange = useCallback(() => {
    recalcAll();
  }, [recalcAll]);

  const handleGWRateChange = useCallback(() => {
    recalcAll();
  }, [recalcAll]);

  // ── Detail G.W. blur: recalculate N.W. (BR-C04) ──
  const handleGWBlur = useCallback((rowIdx: number) => {
    setDetails(prev => {
      const next = [...prev];
      const row = next[rowIdx];
      if (!row) return prev;
      const gw = row.fp611_43 || 0;
      const nw = gw ? Math.round((gw + (row.fp611_442 || 0)) * 10) / 10 : 0;
      next[rowIdx] = { ...row, fp611_44: nw };
      // Update master totals
      const totals = recalcTotals(next);
      setField('fp610_17', totals.fp610_17);
      setField('fp610_18', totals.fp610_18);
      setField('fp610_19', totals.fp610_19);
      setField('fp610_20', totals.fp610_20);
      return next;
    });
  }, [setDetails, setField]);

  // ── Detail MTS blur: round to 2 decimals (BR-C03) ──
  const handleMTSBlur = useCallback((rowIdx: number) => {
    setDetails(prev => {
      const next = [...prev];
      const row = next[rowIdx];
      if (!row) return prev;
      next[rowIdx] = { ...row, fp611_42: Math.round((row.fp611_42 || 0) * 100) / 100 };
      const totals = recalcTotals(next);
      setField('fp610_18', totals.fp610_18);
      return next;
    });
  }, [setDetails, setField]);

  // ── Weight variance blur: recalculate (BR-C04) ──
  const handleWeightVarBlur = useCallback((rowIdx: number) => {
    setDetails(prev => {
      const next = [...prev];
      const row = next[rowIdx];
      if (!row) return prev;
      next[rowIdx] = recalcDetail(row, formData);
      const totals = recalcTotals(next);
      setField('fp610_17', totals.fp610_17);
      setField('fp610_18', totals.fp610_18);
      setField('fp610_19', totals.fp610_19);
      setField('fp610_20', totals.fp610_20);
      return next;
    });
  }, [setDetails, setField, formData]);

  return (
    <div className="p251-form">
      <div className="page-wrapper">
        {/* Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <h1>內外銷PACKING LIST</h1>
          </div>
          <div className="page-title-right">
            {record && (
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                單號: {record.fp610_01}
              </span>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <div className="field-group">
            <label>單據號碼</label>
            <input
              type="text"
              style={{ width: 120 }}
              value={formData.fp610_01 || ''}
              readOnly
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {TABS.map((tab, i) => (
              <button
                key={i}
                className={`tab-nav-item${activeTab === i ? ' active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className={`tab-pane${activeTab === 0 ? ' active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              isEditing={isEditing}
              isPCS={isPCS}
              getField={getField}
              setField={setField}
              onShipUnitChange={handleShipUnitChange}
              onKnitTypeChange={handleKnitTypeChange}
            />
          </div>

          <div className={`tab-pane${activeTab === 1 ? ' active' : ''}`}>
            <Tab2Detail
              formData={formData}
              details={details}
              selectedRow={selectedRow}
              isEditing={isEditing}
              isPCS={isPCS}
              setSelectedRow={setSelectedRow}
              setField={setField}
              setDetailFieldWithCalc={setDetailFieldWithCalc}
              onAddDetail={addDetailRow}
              onDeleteDetail={deleteDetailRow}
              onInsertDetail={insertDetailRow}
              onGWBlur={handleGWBlur}
              onMTSBlur={handleMTSBlur}
              onWeightVarBlur={handleWeightVarBlur}
              onMTSRateChange={handleMTSRateChange}
              onGWRateChange={handleGWRateChange}
            />
          </div>

          <div className={`tab-pane${activeTab === 2 ? ' active' : ''}`}>
            <Tab3GrandUnion
              isEditing={isEditing}
              getField={getField}
              setField={setField}
            />
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <ActionBar
        mode={mode}
        isEditing={isEditing}
        record={record}
        currentIdx={currentIdx}
        allIdsLength={allIds.length}
        permissions={permissions}
        onAdd={handleAdd}
        onModify={handleModify}
        onDelete={handleDeleteAction}
        onNavTo={handleNavTo}
        onExit={handleExit}
      />

      {/* Loading */}
      {loading && (
        <div className="loading-overlay">載入中...</div>
      )}
    </div>
  );
};

export default P251Form;
